import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useRef,
} from "react";
import { Trans, useTranslation } from "react-i18next";

import api from "@helpers/api.js";

import CustomModal from "@components/CustomModal/index.js";
import ShareCommunityModal from "@modals/Community/ShareCommunityModal";

import * as Material from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";

import {Button} from '@fragments'

import communityEvents from "./events.js";
import patterns from "./patterns.js";

const CommunityContext = createContext({ signed: false });

export const CommunityProvider = ({ children }) => {
  const { t } = useTranslation("community");

  const [loading, setLoading] = useState(true);

  const [communities, setCommunities] = useState(null);

  const [Modal_removeMemberOpen, setModal_removeMemberOpen] = useState(null);

  const [Modal_shareCommunity, setModal_shareCommunity] = useState(null);

  const REF_callbackOnLoaded = useRef(() => { });
  const REF_initialized = useRef(false);

  useEffect(() => {
    if (!loading && communities && REF_callbackOnLoaded.current) {
      REF_callbackOnLoaded.current(communities);
    }
  }, [communities]);

  useEffect(() => {
    if (loading && !REF_initialized.current) {
      REF_initialized.current = true;
      loadCommunities();
    }
  }, [loading]);

  function prepareCommunity(community) {
    return {
      ...community,
      isAdmin() {
        return ["owner", "creator"].includes(community.role);
      },
    };
  }

  async function loadCommunities(callback = () => { }) {
    setLoading(true);
    try {
      const res = await api.get("/community/list");
      const list = Array.isArray(res?.data) ? res.data : [];
      setCommunities(list.map(prepareCommunity));
      REF_callbackOnLoaded.current = callback;
    } catch (e) {
      console.log(e);
      setCommunities([]);
    }

    setLoading(false);
  }

  function handleOpenRemoveMemberTool(user) {
    setModal_removeMemberOpen({ ...user });
  }

  const community = {
    management: {
      open(info) {
        window.navigate(`${info.slug}/settings`);
      },
      openGroup(info) {
        communityEvents.triggerModal.configGroup(info);
      },
      async create() {
        try {
          const { data } = await api.post("community", {
            name: t('management.defaultCommunityName'),
          });
          if (data.id) {
            await loadCommunities(() => window.navigate("/" + data.slug))
          }
        } catch (e) {

        }
      },
    },

    // Opens the share card. Takes a loaded community — the community page has
    // the whole payload even for a visitor who isn't a member — or an id, which
    // only resolves against the communities the user belongs to.
    triggerShareModal(target) {
      const c = target && typeof target === 'object' ? target : this.get(target);
      if (c?.slug) setModal_shareCommunity(c);
    },

    open(id) {
      window.navigate("/" + id);
    },

    get(id) {
      return communities?.filter((c) => c.id === id)[0];
    },

    getBySlug(slug) {
      return communities?.filter((c) => c.slug === slug)[0];
    },

    async subscribe(id, callback) {
      const { data } = await api.post(`community/${id}/join`);
      callback();
      return data;
    },
    async unsubscribe(id, callback) {
      const { data } = await api.delete(`community/${id}/leave`);
      callback();
      return data;
    },

    async load(value, key = 'id') {
      try {
        const { data } = await api.get(`community?${key}=${value}`);
        return prepareCommunity(data);
      } catch (e) {
        console.log(e);
      }
    },


    group: {
      pattern: patterns.group,

      get(id) {
        console.log(communities, id);
        return communities?.filter((c) => c.groups?.filter((g) => g.id === id)[0])[0]?.groups?.filter((g) => g.id === id)[0];
      },
    },

    hasAtLeastOneAdminPermission:
      communities?.filter((c) => c.isAdmin())?.length > 0,
  };

  return (
    <CommunityContext.Provider
      value={{
        loading,
        setLoading,

        communities,

        community,

        loadCommunities,

        handleOpenRemoveMemberTool,
      }}
    >
      {children}

      <ModalRemoveMember
        key={Math.random()}
        open={!!Modal_removeMemberOpen}
        setOpen={setModal_removeMemberOpen}
        user={Modal_removeMemberOpen}
      />

      <ShareCommunityModal
        open={!!Modal_shareCommunity}
        community={Modal_shareCommunity}
        onClose={() => setModal_shareCommunity(null)}
      />
    </CommunityContext.Provider>
  );
};

export default CommunityContext;

export function useCommunity() {
  const context = useContext(CommunityContext);

  if (!context) {
    throw new Error("useCommunity must be used within an CommunityProvider.");
  }

  return context;
}

const ModalRemoveMember = ({ open, setOpen, user }) => {
  const { t } = useTranslation("community");

  const [confirmText, setConfirmText] = useState();
  const [loading, setLoading] = useState(false);

  const firstName = user?.username?.split(" ")[0];

  const { currentChallenge } = useCommunity();

  async function handleRemoveUser() {
    setLoading(true);

    try {
      const result = await api.delete("challenge/members", {
        challenge_id: user.challenge_id,
        user_id: user.id,
      });

      if (result.data.success) {
        window.setSnackbar(t("removeMember.success"));
        setOpen(false);
        user.onDelete();
      } else {
        window.setSnackbar(t("removeMember.error"));
      }
    } catch (e) {
      window.setSnackbar(t("removeMember.error"));
    }

    setLoading(false);
  }

  return (
    <CustomModal
      maxWidth={400}
      bottomButtonCancelText={t("common:actions.cancel")}
      hideSecondaryButton={false}
      loading={false}
      title={t("removeMember.title")}
      handleSubmit={() => { }}
      open={open}
      handleClose={() => setOpen(false)}
      width="100%"
      bodyWrapperStyle={{}}
      bodyComponent={
        <div className="container">
          <h3>{t("removeMember.attention")}</h3>
          <h4 className="mt-1 pb-1">
            {t("removeMember.question", { name: user?.username })}
          </h4>
          <label className="pt-1">
            {t("removeMember.warning")}
          </label>
          <label className="pt-4">
            <Trans
              t={t}
              i18nKey="removeMember.confirmHint"
              values={{ name: firstName }}
              components={{ em: <em /> }}
            />
          </label>
          <Material.TextField
            value={confirmText}
            onChange={(v) => setConfirmText(v.target.value)}
            placeholder={firstName}
            fullWidth
          />
          <div className="bottom-buttons-delete-member-modal mt-3">
            <Button
              onClick={handleRemoveUser}
              label={t("removeMember.confirmButton", { name: firstName })}
              color="error"
              endIcon={<DeleteIcon color="error" />}
              disabled={
                firstName?.toLowerCase() !== confirmText?.toLowerCase() ||
                loading
              }
            />
          </div>{" "}
        </div>
      }
      modalClass="select-challenge-modal"
      bottomButtonsOrientation="vertical"
    />
  );
};
